import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IdCard, UserRound, FileText, Megaphone, CalendarDays, Users2, Settings, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const MENU = [
  { to: "/app/kad", label: "Kad Ahli Digital", icon: IdCard },
  { to: "/app/profil", label: "Profil Saya", icon: UserRound },
  { to: "/app/status", label: "Status Keahlian", icon: FileText },
  { to: "/app/dokumen", label: "Dokumen Saya", icon: FileText },
  { to: "/app/pengumuman", label: "Pengumuman", icon: Megaphone },
  { to: "/app/program", label: "Program", icon: CalendarDays },
  { to: "/app/jawatankuasa", label: "Jawatankuasa", icon: Users2 },
  { to: "/app/tetapan", label: "Tetapan", icon: Settings },
];

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("ms-MY", { day: "2-digit", month: "long", year: "numeric" });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [member, setMember] = useState(null);
  const [application, setApplication] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/members/me")
      .then((r) => setMember(r.data))
      .catch(() => api.get("/applications/me").then((r) => setApplication(r.data)).catch(() => {}))
      .finally(() => setLoading(false));
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (loading) return <div className="p-8 text-center text-piks-muted">Memuatkan dashboard...</div>;

  if (!member) {
    return (
      <div className="p-6 sm:p-10 max-w-lg mx-auto text-center" data-testid="dashboard-no-membership-state">
        <FileText className="w-12 h-12 text-piks-gold mx-auto mb-4" />
        <h1 className="font-display text-xl font-bold text-piks-black mb-2">Belum Ada Keahlian Berdaftar</h1>
        {application ? (
          <>
            <StatusBadge status={application.status} className="mx-auto mb-4" />
            <p className="text-sm text-piks-muted mb-6">Permohonan anda kini berstatus di atas. Sila semak semula selepas beberapa hari bekerja.</p>
          </>
        ) : (
          <p className="text-sm text-piks-muted mb-6">Anda belum memohon keahlian PPPS. Mulakan permohonan sekarang.</p>
        )}
        <Link to="/daftar" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-piks-black text-white text-sm font-semibold" data-testid="dashboard-mohon-link">
          {application ? "Sambung Permohonan" : "Mohon Keahlian"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="member-dashboard-page">
      <div className="bg-piks-black text-white px-6 py-10 sm:px-10">
        <p className="text-white/60 text-sm mb-1">Selamat Datang,</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mb-5" data-testid="dashboard-welcome-name">{user?.name}</h1>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm">
            <ShieldCheck className="w-4 h-4" /> {member.status === "ACTIVE" ? "KEAHLIAN AKTIF" : member.status}
          </div>
          <div>
            <p className="text-[11px] text-white/40 uppercase tracking-wide">No. Ahli</p>
            <p className="font-mono text-piks-gold font-semibold" data-testid="dashboard-member-number">{member.member_number}</p>
          </div>
          <div>
            <p className="text-[11px] text-white/40 uppercase tracking-wide">Expiry</p>
            <p className="font-semibold">{formatDate(member.valid_until)}</p>
          </div>
        </div>
      </div>

      <div className="p-6 sm:p-10">
        <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-4">Menu Pantas</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {MENU.map((m) => (
            <Link key={m.to} to={m.to} data-testid={`dashboard-quick-menu-${m.label.toLowerCase().replace(/\s/g, "-")}`} className="p-5 rounded-xl bg-white border border-piks-border hover:border-piks-gold/60 hover:shadow-md transition-all flex flex-col items-center gap-2.5 text-center">
              <m.icon className="w-5 h-5 text-piks-black" />
              <span className="text-xs font-medium text-piks-black leading-tight">{m.label}</span>
            </Link>
          ))}
        </div>

        {announcements.length > 0 && (
          <>
            <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-4">Pengumuman Terkini</h2>
            <div className="space-y-3">
              {announcements.map((a) => (
                <div key={a.id} className="p-4 rounded-lg bg-white border border-piks-border">
                  <p className="font-medium text-sm text-piks-black">{a.title}</p>
                  <p className="text-xs text-piks-muted mt-1 line-clamp-2">{a.body}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
